import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../../components";
import { NAVBAR_ITEMS } from "../../consts";
import {
  useAuthStore,
  useCurrentCommunityCluster,
  useNavbarStore
} from "../../stores";

/** NOTE:
 * issue with the props types for BottomNavigationAction with styled:
 * the href prop isn't deetected properly
 * cfr. @mui/material/ButtonBase/ButtonBase.d.ts: "This component has an additional
 * overload if the `href` prop is set which can make extension quite tricky"
 */
// const StyledBottomNavigationAction = styled(BottomNavigationAction)(() => ({
//   textTransform: "capitalize"
// }));


export function Navbar() {
  const theme = useTheme();
  const { t } = useTranslation();
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const navbarItem = useNavbarStore(state => state.item);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const enableCommunityFlag = useAuthStore(state => state.corporateInfo?.enable_community_flag);
  const { availableClusters: clusters } = useCurrentCommunityCluster();
  const navbarItems = getNavbarItems({ size: isMobile ? "1.5rem" : "1rem" });
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getActiveItem = (link: string) => {
    return pathname.startsWith(link);
  };

  const desktopLabel = (label:string) => !isMobile ? (
    <span aria-hidden="true">
      { t(label) }
    </span>
  ) : <></>;


  return (
    <Box
      component="div"
      role="navigation"
      id="navbar"
      zIndex={ isMobile ? theme.zIndex.drawer : 1 }
      sx={ {
        animation:`effectExit 400ms ${theme.transitions.easing.easeInOut}` ,

        "&#navbar-clone": {
          "& .active-tutorial": {
            position: "relative",
            color: theme.customColors.backgroundAccent,
            [theme.breakpoints.down("md")]: {
              marginBottom: "0"
            },

            "& svg *": {
              fill: theme.customColors.backgroundAccent
            },

            "& .MuiBottomNavigationAction-label": {
              color: theme.customColors.backgroundAccent,
              fontWeight: 700
            }
          }
        },

        "@keyframes effectExit": {
          " 0%": {

            transform: "translateY(0)"
          },
          "100%":{
            transform: "translateY(-200%)"
          }
        }
      } }
    >
      <Divider aria-hidden="true" />
      <BottomNavigation
        id="bottom-navigation"
        tabIndex={ -1 }
        component={ "div" }
        role="navigation"
        aria-label={ t("bottom_navigation_aria_label") }
        data-testid="bottom-navigation-root"
        showLabels
        sx={ {
          borderBottom:`1px solid ${theme.customColors.border}`,
          borderTop:`1px solid ${theme.customColors.border}`,
          gap:theme.spacing(8),
          marginBlock: "0",
          minHeight:"4.5rem",
          padding:"0 16px",
          height:"unset",
          ".MuiBottomNavigationAction-root": {
            flex:0,
            gap:"6px",
            justifyContent:"space-between",
            margin: "0 ",
            maxWidth: "unset",
            minWidth:"unset",
            padding:"0px",
            paddingTop: "12px",
            position:"relative",
            width:"fit-content",

            "&:hover,&:focus-visible":{
              "svg path": {
                fill: theme.customColors.accentVariantA
              },

              "span": {
                color: theme.customColors.accentVariantA
              }
            },

            "&:after":{
              height:"3.33px",
              width:"100%",

              background:theme.customColors.accentVariantA,
              borderRadius:"2px",
              content:"''",
              margin:"5px 0 0",
              opacity:0,
              transition:"all .3s linear"
            },

            "&.isActiveNavigationItem" : {
              color: `${theme.customColors.accentVariantA} !important`,

              ".MuiBottomNavigationAction-label": {
                color:theme.customColors.accentVariantA,
                fontWeight:"700"
              },
              "svg path": {
                fill: `${theme.customColors.accentVariantA}`
              },

              "&:after":{
                opacity: isMobile ? 0 : 1
              }
            },

            ".MuiBottomNavigationAction-label": {
              color: theme.customColors.systemPrimary01,
              fontWeight:"700", // Change for Accessibity report with AB
              textTransform: "capitalize",
              transition:"color .3s linear",
              whiteSpace:"nowrap"
            },

            "svg path":{
              fill: theme.customColors.systemPrimary01,
              transition:"color .3s linear, fill .3s linear"
            }
          },
          // target the labels inside the BottomNavigationAction-root class
          "span > span":{
            fontSize:"1rem !important"
          },
          [theme.breakpoints.down("sm")]:{
            bottom:"0",
            gap:"10px",
            height:"62px",
            justifyContent: "space-evenly",
            left:"0",
            minHeight:"2.5rem",
            overflowX:"hidden",
            padding:"0 20px",
            position:"fixed",
            right:"0",
            width: "100%",
            zIndex:" 999999",

            ".MuiBottomNavigationAction-root" : {
              "&:after" : {
                background: "unset",
                margin: "0"
              },
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              lineHeight: "20px",
              minHeight:"2.5rem",

              "&.isActiveNavigationItem" : {
                borderBottom: 0,
                borderTop:`0.15625rem solid ${theme.customColors.accentVariantA}`,

                ".MuiBottomNavigationAction-label": {
                  color:theme.customColors.accentVariantA,
                  fontWeight:"500"
                }
              },

              ".MuiBottomNavigationAction-label": {
                color: theme.customColors.systemPrimary01,
                fontSize:"12px",
                fontWeight:400,
                lineHeight:"14px",
                textTransform: "capitalize",
                transition:"color .3s linear",
                whiteSpace:"nowrap"
              }
            }
          }
        } }
      >
        {
          navbarItems.map(({ href, icon, item, label }) => {
            // const isActive = location.pathname === href || ( location.pathname.includes(href) && href !== "/") ;
            const classes = (getActiveItem(href)) ? "isActiveNavigationItem" : undefined;

            const NAV_ITEM_META: Record<string, { ariaLabelKey: string; fallbackKey: string; sectionId: string }> = {
              "agenda":    { ariaLabelKey: "bottom_navigation_agenda_aria_label",    fallbackKey: "go_to_agenda",    sectionId: "agenda" },
              "bookmarks": { ariaLabelKey: "bottom_navigation_bookmarks_aria_label", fallbackKey: "go_to_bookmarks", sectionId: "bookmarks" },
              "community": { ariaLabelKey: "bottom_navigation_community_aria_label", fallbackKey: "go_forYou",       sectionId: "community" },
              "explore":   { ariaLabelKey: "bottom_navigation_explore_aria_label",   fallbackKey: "go_to_explore",   sectionId: "explore" },
              "for_you":   { ariaLabelKey: "bottom_navigation_for_you_aria_label",   fallbackKey: "go_forYou",       sectionId: "for-you" }
            };
            const meta = NAV_ITEM_META[label] ?? { ariaLabelKey: "", fallbackKey: "go_forYou", sectionId: label };
            const actionDescription = meta.ariaLabelKey ? t(meta.ariaLabelKey) : t(meta.fallbackKey);
            const navTestId = `bottom-navigation-${meta.sectionId}`;

            return (
              <BottomNavigationAction
                sx={ {
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${theme.customColors.accentVariantA}`,
                    outlineOffset: "2px",
                    borderRadius: "2px"
                  }
                } }
                aria-current={ navbarItem === item ? "page" : undefined }
                aria-label={ actionDescription }
                component={ "div" }
                disableRipple
                key={ `navigation-item--${label}` }
                className={ classes }
                icon={ icon }
                label={ desktopLabel(label) }
                LinkComponent={ "a" }
                onClick={ () => { setNavbarItem(item); navigate(href) } }
                onKeyUp={ (e) => { if( e.key === "Enter" ) { setNavbarItem(item); navigate(href) } } }
                id={ navTestId }
                data-testid={ navTestId }
              />
            );
          })
        }
      </BottomNavigation>
      <Divider  aria-hidden="true"/>
    </Box>
  );

  function getNavbarItems({ size }: { size: string }) {
    let items = [
      {
        href: "/per-te",
        icon: <Icon aria-hidden="true" icon={ "Icons_Per-te" } size={ size } />,
        item: NAVBAR_ITEMS.FOR_YOU,
        label: "for_you"
      },
      {
        href: "/esplora",
        icon:<Icon aria-hidden="true" icon={ "esplora" } size={ size } />,
        item: NAVBAR_ITEMS.EXPLORE,
        label: "explore"
      },
      {
        href: "/bookmarks",
        icon: <Icon aria-hidden="true" icon={ "Icons_Preferiti" } size={ size } />,
        item: NAVBAR_ITEMS.BOOKMARKS,
        label: "bookmarks"
      },
      {
        href: "/community",
        icon: <Icon aria-hidden="true" icon={ "community" } size={ size } />,
        item: NAVBAR_ITEMS.COMMUNITY,
        label: "community"
      },
      {
        href: "/agenda",
        icon: <Icon aria-hidden="true" icon={ "agenda" } size={ size }/>,
        item: NAVBAR_ITEMS.AGENDA,
        label: "agenda"
      }
    ];

    // eslint-disable-next-line no-constant-condition
    if (smartConfiguration?.visForyou === false) {
      items = items.filter((item) => item.item !== NAVBAR_ITEMS.FOR_YOU);
    }
    if (smartConfiguration?.funcFavourites === false) {
      items = items.filter((item) => item.item !== NAVBAR_ITEMS.BOOKMARKS);
    }
    if(
      enableCommunityFlag === false
      || (enableCommunityFlag === true && smartConfiguration?.funcCommunity === false) || (clusters.length == 0)
    ) {
      items = items.filter((item) => item.item !== NAVBAR_ITEMS.COMMUNITY);
    }
    if (smartConfiguration?.widgetCalendar === false) {
      items = items.filter((item) => item.item !== NAVBAR_ITEMS.AGENDA);
    }

    return items;
  }
}
